import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkle,
  Clock,
  Users,
  ArrowLeft,
  GraduationCap,
  CheckCircle,
} from '@phosphor-icons/react';
import { PROGRAMS } from '../data/programs';
import { FEATURES } from '../components/featureFlags';

const visiblePrograms = FEATURES.SHOW_UPCOMING_PROGRAMS
  ? PROGRAMS
  : PROGRAMS.filter((p) => p.statusType !== 'upcoming');

const ProgramsPage = () => (
  <main
    data-testid="programs-page"
    className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
    dir="rtl"
  >
    {/* Hero */}
    <section className="bg-[#1F3D2B] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-5">
            البرامج المهنية
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-5">
            برامج مهنية متكاملة في الطفولة المبكرة
          </h1>
          <p className="text-base sm:text-lg text-[#D8E0C9] leading-9 max-w-3xl">
            برامج وفلسفات تربوية تطبيقية ومناهج تخصصية مصمّمة لتطوير الكفاءات
            التعليمية والقيادية في قطاع الطفولة المبكرة — تختلف عن النماذج
            الجاهزة بكونها مسارات تعلم متكاملة طويلة المدى.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Programs grid */}
    <section className="py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            كتالوج البرامج
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            استكشف برامجنا المهنية
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            نوفّر برامج متخصصة تنمو تدريجياً لتشمل فلسفات تربوية، برامج قيادة،
            برامج جودة، وبرامج إعداد مدربين.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {visiblePrograms.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`program-card-${p.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="bg-white border border-[#E6E0D6] rounded-[24px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="relative h-44 sm:h-52 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <span
                  className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold ${
                    p.statusType === 'available'
                      ? 'bg-[#4A641F] text-white'
                      : 'bg-white/90 text-[#1F3D2B]'
                  }`}
                >
                  {p.status}
                </span>
                <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-medium bg-black/40 text-white backdrop-blur-sm">
                  {p.category}
                </span>
              </div>

              <div className="p-6 sm:p-7">
                <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic'] leading-tight">
                  {p.title}
                </h3>
                {p.titleEn && (
                  <p className="text-xs text-[#92A082] mb-3" dir="ltr">
                    {p.titleEn}
                  </p>
                )}
                <p className="text-sm text-[#6B7565] leading-7 mb-4">{p.summary}</p>

                <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#3D512D] mb-5">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={14} weight="duotone" className="text-[#4A641F]" />
                    {p.duration}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14} weight="duotone" className="text-[#4A641F]" />
                    {p.audience}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Link
                    to={p.href}
                    data-testid={`program-details-btn-${p.id}`}
                    className="flex-1 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white rounded-xl py-2.5 text-sm font-medium transition-colors text-center"
                  >
                    {p.statusType === 'available' ? 'تفاصيل البرنامج' : 'سجّل اهتمامك'}
                  </Link>
                  {p.statusType === 'available' && (
                    <Link
                      to={`/quotation?service=program-${p.id}`}
                      data-testid={`program-enroll-btn-${p.id}`}
                      className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
                    >
                      اطلب عرض سعر
                      <ArrowLeft size={14} className="rtl-flip" />
                    </Link>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>

    {/* Footer note */}
    <section className="py-12 bg-[#F5F2EC]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <Sparkle size={28} weight="duotone" className="text-[#4A641F] mx-auto mb-3" />
        <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
          هل تبحثون عن برنامج مخصص؟
        </h3>
        <p className="text-sm text-[#6B7565] leading-8 mb-5">
          نصمم برامج مخصصة لكل منشأة وفق احتياجاتها التطويرية. تواصلوا معنا
          لمناقشة احتياج جهتكم.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-6 py-3 text-sm font-bold transition-colors"
        >
          تواصل معنا
          <ArrowLeft size={16} className="rtl-flip" />
        </Link>
      </div>
    </section>

    {/* IP notice */}
    <section className="py-8 bg-white">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <p className="text-xs sm:text-sm text-[#6B7565] leading-8">
          جميع البرامج المهنية ومحتوياتها (المواد التدريبية، التصميمات،
          الأدوات، التقييمات) محمية بموجب قوانين الملكية الفكرية، وتمثّل ملكية
          حصرية لـ Child Horizons — آفاق الطفولة. لا يجوز نسخها أو إعادة
          توزيعها أو إعادة بيعها أو نشرها بأي وسيلة دون{' '}
          <strong className="text-[#1F3D2B]">إذن خطي مسبق</strong> من المنصة.{' '}
          <Link to="/legal/intellectual-property" className="text-[#4A641F] hover:underline">
            راجع سياسة الملكية الفكرية
          </Link>
          .
        </p>
      </div>
    </section>
  </main>
);

export default ProgramsPage;
